import { MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Namespace, useTranslation, UseTranslationOptions } from 'react-i18next';

import { UseSelectable, UseToggleable } from './types';
import { Log } from './sentry';
import { useConfig } from './context';
import { blurActiveElement, CORPORATE_CARD, getOffsetTop, useIsDesktop, useIsMobile } from './utils';
import { CURRENCY_KEY, getCurrency, setCurrency as setCurrencyToCache } from './cache';
import { Currency } from './enums';
import { UserLoyaltyCardLevels } from '@websky/graphql';

export const useToggleable: UseToggleable = initialState => {
	const [isOpen, setOpen] = useState(initialState);

	const open = useCallback(() => {
		setOpen(true);
	}, [isOpen]);

	const close = useCallback(() => {
		setOpen(false);
	}, [isOpen]);

	const toggle = useCallback(() => {
		setOpen(!isOpen);
	}, [isOpen]);

	return {
		isOpen,
		open,
		close,
		toggle,
		setOpen
	};
};

export function useSelectable<Enum>(initial: Enum): ReturnType<UseSelectable<Enum>> {
	const [active, setActive] = useState(initial);

	const onSelect = useCallback(
		(newActive: Enum) => {
			setActive(newActive);

			return newActive;
		},
		[active]
	);

	return {
		active,
		onSelect
	};
}

export const useCurrency = (): { currency: Currency; setCurrency: (currency: Currency) => void } => {
	const [currency, setCurrencyState] = useState(getCurrency);

	const setCurrency = useCallback((currency: Currency) => {
		setCurrencyToCache(currency);
		setCurrencyState(currency);
	}, []);

	useEffect(() => {
		const onChangeLocalStorage = (e: StorageEvent) => {
			if (e.key === CURRENCY_KEY && e.oldValue !== e.newValue) {
				setCurrencyState(getCurrency());
			}
		};
		window.addEventListener('storage', onChangeLocalStorage);

		return () => window.removeEventListener('storage', onChangeLocalStorage);
	}, []);

	return { currency, setCurrency };
};

export const useIataCode = (): string => {
	try {
		const {
			global: {
				companyInfo: { iataCode }
			}
		} = useConfig();

		return iataCode;
	} catch {
		return window.__webskyEngineConfig?.global?.companyInfo?.iataCode;
	}
};

export const useShowTaxesInfo = (): boolean => {
	return useConfig().global.showTaxesInfo ?? false;
};

export const useConfigRedirects = () => {
	const {
		Engine: { accountURL }
	} = useConfig();

	const onGoToAccountSettings = () => {
		window.open(`${accountURL}/settings`);
	};

	return { onGoToAccountSettings };
};

export const useHasLoyaltyProgram = (): boolean => {
	try {
		const {
			global: {
				companyInfo: { loyaltyProgramRegistrationUrl }
			}
		} = useConfig();

		return !!loyaltyProgramRegistrationUrl;
	} catch {
		return !!window.__webskyEngineConfig?.global?.companyInfo?.loyaltyProgramRegistrationUrl;
	}
};

export const useSentryTranslation = (ns: Namespace, options?: UseTranslationOptions) => {
	const { t: i18nT, i18n, ...translation } = useTranslation(ns, options);

	const t = (key: string) => {
		if (!i18n.exists(key, { ns })) {
			Log({ level: 'warning', message: `The translation key '${ns}:${key}' is missing` });

			return null;
		}

		return i18nT(key);
	};

	return { ...translation, t, i18n };
};

interface PollingOptions {
	interval: number;
	maxQueryCount?: number;
	progressiveIntervalPerQuery?: boolean;
}

export const useConditionalPolling = <T>(fetchFn: () => Promise<T>, condition: boolean, options?: PollingOptions) => {
	const { interval = 3000, maxQueryCount = 3, progressiveIntervalPerQuery } = options;

	const timeoutId = useRef<NodeJS.Timeout>();
	const queryCount = useRef(1);

	const onFetchHandler = useCallback(async () => {
		try {
			await fetchFn();
		} catch (e) {
			console.log(e);
		}
	}, [fetchFn]);

	useEffect(() => {
		const polling = async () => {
			await onFetchHandler();

			if (!condition || queryCount.current >= maxQueryCount) {
				clearTimeout(timeoutId.current);
				timeoutId.current = undefined;
				queryCount.current = 1;

				return;
			}

			const timeout = progressiveIntervalPerQuery ? interval * queryCount.current : interval;
			timeoutId.current = setTimeout(polling, timeout);

			queryCount.current++;
		};

		if (condition) {
			polling();
		}

		return () => {
			setTimeout(() => {
				if (timeoutId.current) {
					clearTimeout(timeoutId.current);
					timeoutId.current = undefined;
					queryCount.current = 1;
				}
			});
		};
	}, [onFetchHandler, condition, interval, queryCount, maxQueryCount, progressiveIntervalPerQuery]);
};

export const useOffsetTop = (elem: MutableRefObject<HTMLElement>) => {
	const isDesktop = useIsDesktop();
	const isMobile = useIsMobile();

	const offset = useMemo(() => getOffsetTop(elem.current), [elem.current, isDesktop, isMobile]);

	return offset;
};

export const useCustomCssVariable = (variable: string, value: string) => {
	const variableWithPrefix = `--wx-custom-variable-${variable}`;

	useEffect(() => {
		document.documentElement.style.setProperty(variableWithPrefix, value);

		return () => {
			document.documentElement.style.removeProperty(variableWithPrefix);
		};
	}, [variable, value]);
};

export function useElementHeightCSSVar(variable: string) {
	const ref = useRef<HTMLElement | null>(null);
	const variableWithPrefix = `--wx-custom-variable-${variable}`;
	const initialHeight = useRef<number>(null);

	useEffect(() => {
		const element = ref.current;

		if (!element) {
			return null;
		}

		const updateHeight = () => {
			const height = element.getBoundingClientRect().height;
			document.documentElement.style.setProperty(variableWithPrefix, `${height}px`);
		};

		initialHeight.current = element.getBoundingClientRect().height;
		updateHeight(); // Initial measurement

		const resizeObserver = new ResizeObserver(updateHeight);
		resizeObserver.observe(element);

		return () => {
			resizeObserver.disconnect();
			document.documentElement.style.removeProperty(variableWithPrefix);
		};
	}, [variableWithPrefix]);

	return {
		ref,
		initialHeight: initialHeight.current
	};
}

export const useDebounce = <T>(value: T, timeout: number) => {
	const [state, setState] = useState(value);

	useEffect(() => {
		const handler = setTimeout(() => setState(value), timeout);

		return () => clearTimeout(handler);
	}, [value, timeout]);

	return state;
};

/** Перед закрытием снимает фокус с активного элемента */
export const useBlurActiveElement = (open: boolean) => {
	useEffect(() => {
		if (!open) {
			blurActiveElement();
		}
	}, [open]);
};

type LoyaltyTypes = {
	level?: UserLoyaltyCardLevels;
	number?: string;
	basePath?: string;
};

export const useLoyaltyCardImagePath = ({ level, number, basePath = 'loyalty' }: LoyaltyTypes) => {
	const isCardCorporate = number?.startsWith('70000') || !!localStorage.getItem(CORPORATE_CARD);

	const suffix = isCardCorporate ? '_corporate' : '';
	const filename = level ? `${level}${suffix}.png` : `default${suffix}.png`;

	const path = `${basePath}/${filename}`;
	const fallbackUrl = `${basePath}/default${suffix}.png`;
	const alt = level ? `${level} level` : 'Loyalty level';

	return { path, fallbackUrl, alt, isCardCorporate };
};
